import { template as template_1efa7b1ef4384fe7986184782d71997f } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { isEmpty, isPresent } from '@ember/utils';
import { usesBomSourceLookup, STATION_LOAD_OPTIONS } from 'eflex/constants/station-options';
import { waitFor } from '@ember/test-waiters';
import { waitForEvent, task } from 'ember-concurrency';
import { t } from 'ember-intl';
import BsButton from 'eflex/components/bs-button';
import { fn } from '@ember/helper';
import JemProductionScheduleLink from 'eflex/components/jem/production-schedule-link';
import JemModelDropdown from 'eflex/components/jem/model-dropdown';
import JemModelDisplay from 'eflex/components/jem/model-display';
import JemMultipieceStandaloneQuantity from 'eflex/components/jem/multipiece-standalone-quantity';
import { and, or } from 'ember-truth-helpers';
import JemKineticJobOpSelector from 'eflex/components/jem/kinetic-job-op-selector';
import JemBomSourceLookupInput from 'eflex/components/jem/bom-source-lookup-input';
import JemSerialInput from 'eflex/components/jem/serial-input';
import JemSerialDisplay from 'eflex/components/jem/serial-display';
import JemBomVariables from 'eflex/components/jem/bom-variables';
import JemCustomIdentifierData from 'eflex/components/jem/custom-identifier-data';
import JemBuildStatus from 'eflex/components/jem/build-status';
import JemEndActivityModal from 'eflex/components/jem/end-activity-modal';
import { on } from '@ember/modifier';
import { set } from 'eflex/helpers';
import Modifier from 'ember-modifier';
class OnIframeMessageFocus extends Modifier {
    #didSetup = false;
    modify(element) {
        if (this.#didSetup) {
            return;
        }
        this.#didSetup = true;
        this._messageListener.perform(element);
    }
    _messageListener = task({
        onState: null
    }, async (element)=>{
        while(true){
            const event = await waitForEvent(globalThis, 'message');
            if (event.data?.focusChildSelector) {
                element.querySelector(event.data?.focusChildSelector)?.focus();
            } else if (event.data?.focusElement) {
                element.focus();
            }
        }
    });
}
const getShowModelDropdown = (station, stationIsRunning)=>{
    if (stationIsRunning) {
        return false;
    }
    return (station?.loadOption === STATION_LOAD_OPTIONS.modelGenerateSerialNumber || station?.loadOption === STATION_LOAD_OPTIONS.selectModel);
};
const getShowEndActivity = (station, stationIsRunning, notAuthorized, kineticAssemblyOperation)=>{
    const isSinglePieceWithQuantityToReport = !kineticAssemblyOperation?.operation?.isMultipiece && kineticAssemblyOperation?.hasQuantitiesToReport;
    return station.usesOperations && !stationIsRunning && !notAuthorized && isPresent(kineticAssemblyOperation) && isSinglePieceWithQuantityToReport;
};
const getSerialInputDisabled = (station, kineticAssemblyOperation, notAuthorized)=>{
    return (station.usesOperations && isEmpty(kineticAssemblyOperation)) || notAuthorized;
};
const getShowSerialInput = (station, model, stationIsRunning, waitingForData)=>{
    if (model == null && !station.usesOperations && getShowModelDropdown(station, stationIsRunning)) {
        return false;
    }
    return !stationIsRunning && !waitingForData;
};
class JemInfoBar extends Component {
    @service
    currentUser;
    @tracked
    showEndActivityModal = false;
    onEndActivity = task(waitFor(async (args)=>{
        await this.args.onEndActivity(args);
        this.showEndActivityModal = false;
    }));
    static{
        template_1efa7b1ef4384fe7986184782d71997f(`
    <div
      class="jem-info-bar row bg-gray-900 p-2 px-1 pt-0 pb-3"
      {{OnIframeMessageFocus}}
      ...attributes
    >
      {{#if @station.isStandaloneMultipiece}}
        <JemMultipieceStandaloneQuantity @buildStatus={{@buildStatus}} />
      {{/if}}

      {{#if @station.usesModels}}
        {{#if @station.productionScheduleEnabled}}
          <div class="col-auto d-flex align-items-center mt-4 pb-1">
            <JemProductionScheduleLink
              @disabled={{this.currentUser.isNotAdmin}}
              @productionSchedule={{@productionSchedule}}
              @stationIsRunning={{@stationIsRunning}}
            />
          </div>
        {{/if}}

        <div class="col-4">
          <label class="form-label">
            {{t "model"}}
          </label>

          {{#if (getShowModelDropdown @station @stationIsRunning)}}
            <JemModelDropdown
              @models={{@station.area.models}}
              @model={{@model}}
              @productionScheduleEnabled={{@station.productionScheduleEnabled}}
              @disabled={{or
                @stationIsRunning
                (or @notAuthorized @station.productionScheduleEnabled)
              }}
              @onChange={{@onModelSelected}}
              @onScan={{@onModelScanned}}
            />
          {{else}}
            <JemModelDisplay
              @productionScheduleEnabled={{@station.productionScheduleEnabled}}
              @productionScheduleModelCode={{@productionSchedule.model.code}}
              @buildStatusModelCode={{@buildStatus.modelName}}
            />
          {{/if}}
        </div>
      {{else if @station.usesOperations}}
        <JemKineticJobOpSelector
          @station={{@station}}
          @selectedJob={{@selectedJob}}
          @selectedAssembly={{@selectedAssembly}}
          @assemblyOperation={{@kineticAssemblyOperation}}
          @onJobSelected={{@onJobSelected}}
          @onAssemblySelected={{@onAssemblySelected}}
          @onOperationSelected={{@onOperationSelected}}
          @disabled={{or @stationIsRunning @notAuthorized}}
        />
      {{else if (usesBomSourceLookup @station)}}
        <JemBomSourceLookupInput
          disabled={{@notAuthorized}}
          @bomSourceLookupValue={{@bomSourceLookupValue}}
          @stationIsRunning={{@stationIsRunning}}
          @buildStatusBomSourceLookupValue={{@buildStatus.bomSourceLookupValue}}
          @tempSerialNumber={{@tempSerialNumber}}
          @onEnter={{fn @onSubmitSerialNumber @tempSerialNumber}}
          @onInput={{@onBomSourceLookupValueInput}}
          {{on "focusin" @onClearSelectedTask}}
        />
      {{/if}}
      <div class="col d-flex align-items-end">
        {{#if (getShowSerialInput @station @model @stationIsRunning @waitingForData)}}
          <JemSerialInput
            @disabled={{getSerialInputDisabled @station @kineticAssemblyOperation @notAuthorized}}
            @serialNumber={{@buildStatus.serialNumber}}
            @tempSerialNumber={{@tempSerialNumber}}
            @bomSourceLookupValue={{@bomSourceLookupValue}}
            @station={{@station}}
            @modelCode={{@model.code}}
            @kineticJobNumber={{@kineticAssemblyOperation.job.jobNumber}}
            @onSubmit={{@onSubmitSerialNumber}}
            @onInput={{@onSerialNumberInput}}
            @onScan={{@onSerialNumberScanned}}
            {{on "focusin" @onClearSelectedTask}}
          />
        {{else}}
          <JemSerialDisplay @buildStatus={{@buildStatus}} />
        {{/if}}
      </div>

      {{#if (getShowEndActivity @station @stationIsRunning @notAuthorized @kineticAssemblyOperation)}}
        <div class="col-auto d-flex align-items-end">
          <BsButton
            class="end-activity text-capitalize"
            @type="primary"
            @size="lg"
            @onClick={{set this 'showEndActivityModal' true}}
          >
            {{t "kinetic.endActivity"}}
          </BsButton>
        </div>
      {{/if}}

      {{#if (and @station.usesComponents @station.area.jemDisplayVariable @stationIsRunning)}}
        <JemBomVariables
          class='col-auto'
          @jemDisplayVariable={{@station.area.jemDisplayVariable}}
          @buildDatumVariables={{@buildStatus.buildDatum.variables}}
        />
      {{/if}}

      {{#if (isPresent @buildStatus.customIdentifierData)}}
        <div class='col'>
          <JemCustomIdentifierData
            @customIdentifierData={{@buildStatus.customIdentifierData}}
            @waitingForData={{@waitingForData}}
            @onSubmit={{@submitCustomIdentifierData}}
          />
        </div>
      {{/if}}

      <JemBuildStatus
        class="col-auto align-self-end d-flex flex-column mb-0"
        @statusClass={{@buildStatus.statusClass}}
        @statusTranslation={{@buildStatus.statusTranslation}}
      />
    </div>

    {{#if this.showEndActivityModal}}
      <JemEndActivityModal
        @assemblyOperation={{@kineticAssemblyOperation}}
        @onConfirm={{this.onEndActivity.perform}}
        @onCancel={{set this 'showEndActivityModal' false}}
      />
    {{/if}}
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
export default JemInfoBar;
